import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarPlus, Clock, Calendar as CalendarIcon } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { fmtDateTime } from "@/lib/tz";
import { guestOnboardAt } from "@/lib/guestOnboard";
import { useLiveEvents } from "@/lib/useLiveEvents";

/**
 * On-the-spot booking dialog for the Spa Ops iPad.
 *
 * Rewritten Feb 2026: the previous version used <input type="datetime-local">
 * which ignored the spa's prep_time_min setting and let operators book right
 * up against an existing appointment — the backend then rejected the request
 * with a 409 AFTER the operator had already picked everything. The rewrite
 * borrows the cabin-iPad guest booking pattern: a Calendar for the day, then
 * an Hour grid and a Minute (5-min steps) grid, each button visibly disabled
 * (line-through, greyed) when the slot overlaps a busy interval OR falls
 * inside the prep-time buffer. Matches the "crystal clear available slot"
 * language the yacht crew asked for.
 */

function localStartOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

function durationOptionsFor(treatment) {
  if (!treatment) return [];
  const base = { minutes: Number(treatment.duration_min) || 60, price: Number(treatment.price) || 0, label: "" };
  const extras = (treatment.durations || [])
    .map((d) => ({ minutes: Number(d.minutes) || 0, price: Number(d.price) || 0, label: (d.label || "").trim() }))
    .filter((d) => d.minutes > 0);
  const seen = new Set();
  return [base, ...extras].filter((d) => (seen.has(d.minutes) ? false : (seen.add(d.minutes), true)))
    .sort((a, b) => a.minutes - b.minutes);
}

export default function SpaOpsQuickBookDialog({
  open, onOpenChange, initialAt, initialSeed, guests, treatments, crew, bookings, settings, onCreated,
}) {
  const [form, setForm] = useState({
    guest_id: "", treatment_id: "", crew_id: "",
    date: null, slot: "", notes: "", duration_min: null,
  });
  const [busy, setBusy] = useState(false);
  const [pickedHour, setPickedHour] = useState("");

  // Reset every time the dialog opens with a fresh slot. When the parent
  // supplies `initialSeed` (only fired when the operator taps a CANCELLED
  // booking on the calendar), we pre-fill treatment/crew/duration + jump
  // straight to the hour+minute of the freed-up slot so rebooking is a
  // guest-pick-and-go flow. Empty seed = clean fresh dialog.
  useEffect(() => {
    if (!open) return;
    const seed = new Date(initialAt || Date.now());
    const hour = initialSeed && initialAt ? String(seed.getHours()).padStart(2, "0") : "";
    const slotIso = initialSeed && initialAt ? seed.toISOString() : "";
    setForm({
      guest_id: "",
      treatment_id: (initialSeed && initialSeed.treatment_id) || "",
      crew_id: (initialSeed && initialSeed.crew_id) || "",
      date: localStartOfDay(seed),
      slot: slotIso,
      notes: initialSeed ? "Rebooked on top of cancelled slot" : "",
      duration_min: (initialSeed && initialSeed.duration_min) || null,
    });
    setPickedHour(hour);
  }, [open, initialAt, initialSeed]);

  // Bookings visible to the picker. Seeded from the parent's `bookings` prop
  // (which is scoped to today's calendar range) and then supplemented with
  // per-date fetches when the operator picks a different day. Also refreshed
  // on WebSocket booking events so a walk-in booked on ANOTHER Spa iPad greys
  // out that slot for us in real time — fixes the "two operators just booked
  // the same slot" race that the previous static-prop version allowed.
  const [dayBookings, setDayBookings] = useState([]);
  const fetchDayBookings = useCallback(async (d) => {
    if (!d) { setDayBookings([]); return; }
    const start = new Date(d); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 1);
    try {
      const { data } = await api.get("/admin/bookings", { params: {
        start: start.toISOString(), end: end.toISOString(), order: "asc",
      }});
      setDayBookings(data || []);
    } catch { /* silent — dialog will fall back to parent props */ }
  }, []);
  useEffect(() => {
    if (!open) return;
    fetchDayBookings(form.date);
  }, [open, form.date, fetchDayBookings]);
  // Live-events refresh — any create/update/cancel/delete on the picker's
  // current day refetches so slot availability stays honest across devices.
  useLiveEvents(useCallback((ev) => {
    if (!open || !form.date) return;
    if (!ev || !ev.type || !ev.type.startsWith("booking.")) return;
    const startDay = (ev.booking?.start_at || "").slice(0, 10);
    const pickedKey = form.date.toISOString().slice(0, 10);
    if (startDay === pickedKey) fetchDayBookings(form.date);
  }, [open, form.date, fetchDayBookings]));

  // Merge parent-provided bookings with the dialog's own fetch so we never
  // regress if the parent is already caching today's data. Dedupe by id.
  const effectiveBookings = useMemo(() => {
    const map = new Map();
    for (const b of (bookings || [])) if (b && b.id) map.set(b.id, b);
    for (const b of dayBookings) if (b && b.id) map.set(b.id, b);
    return Array.from(map.values());
  }, [bookings, dayBookings]);

  // Eligible guests filtered by their stay window against the selected slot.
  const slotIso = form.slot || null;
  const eligibleGuests = useMemo(() => {
    if (!slotIso) return guests || [];
    return (guests || []).filter((g) => guestOnboardAt(g, slotIso));
  }, [guests, slotIso]);

  useEffect(() => {
    if (!form.guest_id) return;
    if (!eligibleGuests.some((g) => (g.guest_id || g.id) === form.guest_id)) {
      setForm((f) => ({ ...f, guest_id: "" }));
    }
  }, [eligibleGuests, form.guest_id]);

  const [cabinFilter, setCabinFilter] = useState("");
  // Distinct cabin names across all guests — used to power the cabin
  // picker so operators can pick "cabin first, then guest" like the
  // guest-facing flow. Sorted case-insensitively so Master Suite / Guest
  // Cabin 1 / VIP feel predictable regardless of insertion order.
  const cabinOptions = useMemo(() => {
    const names = new Set();
    for (const g of (guests || [])) {
      if (g && g.cabin_name) names.add(g.cabin_name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [guests]);

  // Reset the cabin filter whenever the dialog opens fresh so a stale
  // pick from a previous flow doesn't shadow the eligible guests.
  useEffect(() => { if (open) setCabinFilter(""); }, [open]);

  const chosenTreatment = useMemo(
    () => (treatments || []).find((t) => t.id === form.treatment_id),
    [treatments, form.treatment_id],
  );

  const durationChoices = useMemo(() => durationOptionsFor(chosenTreatment), [chosenTreatment]);

  useEffect(() => {
    if (!chosenTreatment) { setForm((f) => ({ ...f, duration_min: null })); return; }
    setForm((f) => ({ ...f, duration_min: Number(chosenTreatment.duration_min) || 60 }));
  }, [chosenTreatment]);

  const eligibleCrew = useMemo(() => {
    const activeCrew = (crew || []).filter((c) => c.active !== false);
    if (!chosenTreatment) return activeCrew;
    const assigned = chosenTreatment.crew_ids || [];
    if (!assigned.length) return activeCrew;
    return activeCrew.filter((c) => assigned.includes(c.id));
  }, [crew, chosenTreatment]);

  // The heart of the fix: mirror the cabin-iPad slot generator. Uses the
  // spa's open_time / close_time / prep_time_min / global_min_notice_min from
  // settings so every constraint the backend will enforce is already visible
  // to the operator BEFORE they tap.
  const slots = useMemo(() => {
    if (!form.date) return [];
    const s = settings || {};
    const [oh, om] = String(s.open_time || "09:00").split(":").map(Number);
    const [ch, cm] = String(s.close_time || "20:00").split(":").map(Number);
    if (![oh, om, ch, cm].every((n) => Number.isFinite(n))) return [];
    const dur = Number(form.duration_min) || Number(chosenTreatment?.duration_min) || 60;
    const prep = Number(s.prep_time_min) || 0;
    const minNoticeMin = Number(
      chosenTreatment?.min_notice_min ?? s.global_min_notice_min ?? 0
    ) || 0;
    const cutoff = new Date(Date.now() + minNoticeMin * 60 * 1000);

    // Filter bookings for the picked day so we don't scan the whole month.
    const dayKey = form.date.toISOString().slice(0, 10);
    const dayFiltered = (effectiveBookings || []).filter((b) => {
      if (b.status === "cancelled") return false;
      return (b.start_at || "").slice(0, 10) === dayKey;
    });
    // If the operator picked a specific therapist, ONLY that therapist's
    // schedule matters for the picker — the same logic the guest iPad uses.
    const relevant = form.crew_id
      ? dayFiltered.filter((b) => (b.crew_id || null) === form.crew_id)
      : dayFiltered;

    const out = [];
    const base = new Date(form.date.getTime()); base.setHours(oh, om, 0, 0);
    const close = new Date(form.date.getTime()); close.setHours(ch, cm, 0, 0);
    const stepMin = 5;
    let iter = 0;
    while (base.getTime() + dur * 60000 <= close.getTime() && iter < 500) {
      const start = new Date(base.getTime());
      const end = new Date(base.getTime() + dur * 60000);
      const bufMs = prep * 60000;
      const overlap = relevant.some((b) => {
        const bs = new Date(b.start_at).getTime() - bufMs;
        const be = (b.end_at ? new Date(b.end_at).getTime() : new Date(b.start_at).getTime() + (b.duration_min || 60) * 60000) + bufMs;
        return start.getTime() < be && end.getTime() > bs;
      });
      const tooSoon = start < cutoff;
      out.push({
        iso: start.toISOString(),
        label: start.toTimeString().slice(0, 5),
        disabled: overlap || tooSoon,
        reason: overlap ? "busy" : tooSoon ? "too_soon" : null,
      });
      base.setMinutes(base.getMinutes() + stepMin); iter++;
    }
    return out;
  }, [form.date, form.duration_min, form.crew_id, chosenTreatment, effectiveBookings, settings]);

  const timeGrid = useMemo(() => {
    const byHour = {};
    for (const s of slots) {
      const [hh, mm] = s.label.split(":");
      (byHour[hh] = byHour[hh] || []).push({ ...s, mm });
    }
    return { hours: Object.keys(byHour).sort(), byHour };
  }, [slots]);

  // If the current pickedHour disappears (duration change filled the airspace),
  // clear it.
  useEffect(() => {
    if (form.slot && !slots.some((s) => s.iso === form.slot)) {
      setForm((f) => ({ ...f, slot: "" }));
    }
    if (pickedHour && !timeGrid.hours.includes(pickedHour)) setPickedHour("");
  }, [slots, timeGrid, pickedHour, form.slot]);

  // On first open, if the seed slot maps to a valid hour on this day, pre-open
  // the minute grid so the operator sees the tap-target immediately.
  useEffect(() => {
    if (!open || !initialAt) return;
    const seed = new Date(initialAt);
    const hh = String(seed.getHours()).padStart(2, "0");
    if (timeGrid.hours.includes(hh)) setPickedHour(hh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialAt, timeGrid.hours.length]);

  const chosenDuration = durationChoices.find((d) => d.minutes === form.duration_min);
  const submit = async () => {
    if (!form.guest_id || !form.treatment_id || !form.slot) {
      toast.error("Guest, treatment and time are required"); return;
    }
    setBusy(true);
    try {
      const body = {
        guest_id: form.guest_id,
        treatment_id: form.treatment_id,
        start_at: form.slot,
        duration_min: form.duration_min || undefined,
        crew_id: form.crew_id || null,
        notes: form.notes || "",
      };
      const { data } = await api.post("/admin/bookings/manual", body);
      toast.success(`Booked · ${data.guest_name || "guest"} @ ${new Date(form.slot).toTimeString().slice(0, 5)}`);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Booking failed");
    } finally { setBusy(false); }
  };

  const label = form.slot ? fmtDateTime(form.slot) : (form.date ? form.date.toDateString() : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="spa-ops-quickbook-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-accent" /> On-the-spot booking
          </DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            Log a walk-in appointment{label ? ` for ${label}` : ""}. Slots that overlap an existing booking or fall inside the
            spa's <span className="font-medium">{Number(settings?.prep_time_min) || 0}-minute prep buffer</span> are struck through and can't be tapped.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline text-primary/60">Treatment</Label>
            <select
              value={form.treatment_id}
              onChange={(e) => setForm({ ...form, treatment_id: e.target.value, crew_id: "" })}
              className="mt-2 w-full h-11 border border-border bg-background px-3 text-sm"
              data-testid="qb-treatment"
            >
              <option value="">— select treatment —</option>
              {(treatments || []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {chosenTreatment && durationChoices.length > 0 && (
            <div data-testid="qb-duration-row">
              <Label className="overline text-primary/60 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Duration
                {chosenDuration && !chosenTreatment.price_hidden && (
                  <span className="ml-auto text-accent font-display text-sm normal-case tracking-normal">
                    €{Number(chosenDuration.price).toFixed(0)}
                  </span>
                )}
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {durationChoices.map((d) => {
                  const active = form.duration_min === d.minutes;
                  return (
                    <button
                      key={d.minutes}
                      type="button"
                      onClick={() => setForm({ ...form, duration_min: d.minutes, slot: "" })}
                      className={`px-4 h-10 rounded-full border text-sm transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border hover:border-primary/60 hover:bg-muted"
                      }`}
                      aria-pressed={active}
                      data-testid={`qb-duration-${d.minutes}`}
                    >
                      <span className="font-mono">{d.minutes}</span><span className="opacity-70 ml-1">min</span>
                      {d.label && <span className="ml-1.5 opacity-60 text-[11px]">· {d.label}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="overline text-primary/60">Therapist (optional)</Label>
            <select
              value={form.crew_id}
              onChange={(e) => setForm({ ...form, crew_id: e.target.value, slot: "" })}
              className="mt-2 w-full h-11 border border-border bg-background px-3 text-sm"
              data-testid="qb-crew"
            >
              <option value="">— any available —</option>
              {eligibleCrew.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {form.crew_id && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Availability shown reflects <span className="text-foreground font-medium">{crew.find((c) => c.id === form.crew_id)?.name}</span>'s schedule only.
              </p>
            )}
          </div>

          <div>
            <div className="overline text-primary/60 mb-2 flex items-center gap-1.5"><CalendarIcon className="h-3 w-3" /> Date</div>
            <div className="border border-border bg-card p-3 inline-block">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(d) => { if (d instanceof Date) setForm((f) => ({ ...f, date: localStartOfDay(d), slot: "" })); }}
                data-testid="qb-date"
              />
            </div>
          </div>

          {form.date && (chosenTreatment ? (
            <div data-testid="qb-time-picker">
              <div className="overline text-primary/60 mb-3">Pick a time</div>
              {slots.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="qb-no-slots">
                  No slots available on this date — try another day, therapist or duration.
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Hour</div>
                    <div className="flex flex-wrap gap-2" data-testid="qb-hour-row">
                      {timeGrid.hours.map((h) => {
                        const disabled = (timeGrid.byHour[h] || []).every((s) => s.disabled);
                        const active = pickedHour === h;
                        return (
                          <button
                            key={h}
                            disabled={disabled}
                            onClick={() => { setPickedHour(h); setForm((f) => ({ ...f, slot: "" })); }}
                            className={`min-w-[3.25rem] h-11 px-3 border text-sm font-mono transition-colors ${
                              active ? "bg-primary text-primary-foreground border-primary"
                              : disabled ? "border-border text-muted-foreground/40 line-through cursor-not-allowed"
                              : "border-border hover:border-primary"
                            }`}
                            data-testid={`qb-hour-${h}`}
                          >{h}</button>
                        );
                      })}
                    </div>
                  </div>
                  {pickedHour && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Minutes (5-min steps)</div>
                      <div className="grid grid-cols-6 sm:grid-cols-12 gap-2" data-testid="qb-minute-row">
                        {(timeGrid.byHour[pickedHour] || []).map((s) => {
                          const selected = form.slot === s.iso;
                          return (
                            <button
                              key={s.iso}
                              disabled={s.disabled}
                              onClick={() => setForm((f) => ({ ...f, slot: s.iso }))}
                              className={`h-11 border text-sm font-mono transition-colors ${
                                selected ? "bg-primary text-primary-foreground border-primary"
                                : s.disabled ? "border-border text-muted-foreground/40 line-through cursor-not-allowed"
                                : "border-border hover:border-primary"
                              }`}
                              data-testid={`qb-slot-${s.label}`}
                              title={s.reason === "busy" ? "Overlaps another booking or its prep buffer" : s.reason === "too_soon" ? "Too soon (before min-notice cutoff)" : ""}
                              aria-label={`${pickedHour}:${s.mm}`}
                            >:{s.mm}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {form.slot && (
                <div className="mt-3 text-sm">
                  <span className="text-muted-foreground">Selected:</span>{" "}
                  <span className="font-mono text-primary text-base" data-testid="qb-selected-slot">
                    {new Date(form.slot).toTimeString().slice(0, 5)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">Pick a treatment above to see available slots for this day.</p>
          ))}

          <div>
            <Label className="overline text-primary/60">Cabin</Label>
            <select
              value={cabinFilter}
              onChange={(e) => { setCabinFilter(e.target.value); setForm((f) => ({ ...f, guest_id: "" })); }}
              className="mt-2 w-full h-11 border border-border bg-background px-3 text-sm"
              data-testid="qb-cabin"
            >
              <option value="">All cabins</option>
              {cabinOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="overline text-primary/60">Guest</Label>
            <select
              value={form.guest_id}
              onChange={(e) => setForm({ ...form, guest_id: e.target.value })}
              className="mt-2 w-full h-11 border border-border bg-background px-3 text-sm"
              data-testid="qb-guest"
            >
              <option value="">— select guest —</option>
              {eligibleGuests
                .filter((g) => !cabinFilter || g.cabin_name === cabinFilter)
                .map((g) => (
                  <option key={g.guest_id || g.id} value={g.guest_id || g.id}>
                    {g.name}{g.cabin_name ? ` · ${g.cabin_name}` : ""}{g.charter_name ? ` · ${g.charter_name}` : ""}
                  </option>
                ))}
            </select>
            {(guests || []).length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No guests on-board right now — check the active charter has guests.</p>
            ) : eligibleGuests.length === 0 && form.slot ? (
              <p className="text-xs text-amber-700 dark:text-amber-500 mt-1" data-testid="qb-no-eligible-guests">
                No guests are scheduled to be onboard at this date/time. Adjust the slot or their stay dates in Trips &amp; guests.
              </p>
            ) : eligibleGuests.length < (guests || []).length && form.slot ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                Showing {eligibleGuests.length} of {(guests || []).length} — the rest aren't onboard at this slot.
              </p>
            ) : null}
          </div>

          <div>
            <Label className="overline text-primary/60">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything the therapist should know…"
              className="mt-2"
              data-testid="qb-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qb-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.slot} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="qb-save">
              {busy ? "Booking…" : "Confirm booking"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
